import React, {
  useState,
  useEffect,
  useCallback,
  memo,
  lazy,
  Suspense,
} from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { FileTextIcon, ImageIcon, FileIcon } from '@phosphor-icons/react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { processExcelSheet } from 'src/pages/authenticated/rebates/upload-data/utils/spreadsheetUtils';
import { Num, Plural, T, useGT, Var } from 'gt-react';

// Handsontable + hyperformula are ~575 KB gzipped and only needed when the
// selected file actually is a spreadsheet, which is the rare path here.
const SpreadsheetViewer = lazy(
  () =>
    import('src/pages/authenticated/rebates/upload-data/components/SpreadsheetViewer'),
);

interface RawFilePreviewProps {
  selectedFiles: File[];
  className?: string;
  selectedSheet?: string; // Optional XLSX sheet selection
  isInPopup?: boolean; // Whether component is in popup window
  showCard?: boolean; // Whether to show Card wrapper and title header
  // Override inline-mode height for the spreadsheet/PDF preview area
  // (e.g. "70vh" or "600px"). Ignored when isInPopup is true.
  previewHeight?: string;
}

interface FilePreview {
  name: string;
  size: string;
  type: string;
  extension: string;
  content: string;
  error?: string;
  truncated?: boolean;
  isBinary?: boolean;
  previewUrl?: string; // Object URL for native browser viewing
  isSpreadsheet?: boolean; // Whether to use SpreadsheetViewer
  spreadsheetData?: unknown[][]; // Data for SpreadsheetViewer
  availableSheets?: string[]; // Available sheet names for XLSX
}

const RawFilePreview: React.FC<RawFilePreviewProps> = memo(
  ({
    selectedFiles,
    className = '',
    selectedSheet,
    isInPopup = false,
    showCard = true,
    previewHeight,
  }) => {
    const gt = useGT();
    const [previews, setPreviews] = useState<FilePreview[]>([]);
    const [selectedPreview, setSelectedPreview] = useState<number>(0);
    const [internalSelectedSheet, setInternalSelectedSheet] = useState<
      string | null
    >(null);

    const getFileIcon = (extension: string) => {
      const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff'];
      if (imageExtensions.includes(extension.toLowerCase())) {
        return <ImageIcon className="w-4 h-4" />;
      }
      if (['csv', 'xlsx', 'xls'].includes(extension.toLowerCase())) {
        return <FileTextIcon className="w-4 h-4" />;
      }
      return <FileIcon className="w-4 h-4" />;
    };

    const formatFileSize = useCallback(
      (bytes: number): string => {
        if (bytes === 0) {
          return gt('0 B', { context: 'file size zero bytes' });
        }
        const k = 1024;
        const sizes = [
          gt('B', { context: 'file size unit bytes' }),
          gt('KB', { context: 'file size unit kilobytes' }),
          gt('MB', { context: 'file size unit megabytes' }),
          gt('GB', { context: 'file size unit gigabytes' }),
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
      },
      [gt],
    );

    const createObjectUrl = (file: File): string => {
      return URL.createObjectURL(file);
    };

    // Cleanup object URLs to prevent memory leaks
    useEffect(() => {
      return () => {
        previews.forEach((preview) => {
          if (preview.previewUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(preview.previewUrl);
          }
        });
      };
    }, [previews]);

    // Sync internal sheet selection with parent's selectedSheet prop
    useEffect(() => {
      if (selectedSheet) {
        setInternalSelectedSheet(selectedSheet);
      }
    }, [selectedSheet]);

    // Process files to generate previews
    useEffect(() => {
      if (!selectedFiles.length) {
        setPreviews([]);
        return;
      }

      const processFiles = async () => {
        const newPreviews: FilePreview[] = [];

        for (const file of selectedFiles) {
          const extension = file.name.split('.').pop()?.toLowerCase() || '';
          const preview: FilePreview = {
            name: file.name,
            size: formatFileSize(file.size),
            type:
              file.type || gt('unknown', { context: 'file MIME type unknown' }),
            extension,
            content: '',
          };

          try {
            if (['csv'].includes(extension)) {
              const text = await file.text();

              const result = Papa.parse(text, {
                skipEmptyLines: true,
                delimiter: ',',
                quoteChar: '"',
                escapeChar: '"',
              });

              const csvData = result.data as string[][];

              preview.isSpreadsheet = true;
              preview.spreadsheetData = csvData;
              preview.content = gt(
                'CSV file with {rowCount} rows, {columnCount} columns',
                {
                  rowCount: String(csvData.length),
                  columnCount: String(csvData[0]?.length || 0),
                  context: 'rebate upload file preview spreadsheet summary',
                },
              );
            } else if (['xlsx', 'xls'].includes(extension)) {
              const buffer = await file.arrayBuffer();
              const workbook = XLSX.read(buffer, { type: 'array' });

              preview.availableSheets = workbook.SheetNames;

              // Use effective sheet (internal user selection > parent prop > first sheet)
              const effectiveSheet =
                internalSelectedSheet ||
                selectedSheet ||
                workbook.SheetNames[0];
              const sheetName =
                effectiveSheet && workbook.SheetNames.includes(effectiveSheet)
                  ? effectiveSheet
                  : workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];

              const { processedData } = processExcelSheet(worksheet, {
                sheetName,
                // Don't apply hierarchical grouping for preview - keep it simple
                hierarchicalOptions: {
                  enableHierarchicalGrouping: false,
                },
              });

              preview.isSpreadsheet = true;
              preview.spreadsheetData = processedData;
              preview.content = gt(
                'Excel file - Sheet: {sheetName} ({rowCount} rows, {columnCount} columns)',
                {
                  sheetName,
                  rowCount: String(processedData.length),
                  columnCount: String(processedData[0]?.length || 0),
                  context: 'rebate upload file preview spreadsheet summary',
                },
              );
              if (workbook.SheetNames.length > 1) {
                preview.content += `\n${gt('Available sheets: {sheets}', {
                  sheets: workbook.SheetNames.join(', '),
                  context: 'rebate upload Excel multi-sheet preview',
                })}`;
              }
            } else if (
              ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'pdf'].includes(
                extension,
              )
            ) {
              const objectUrl = createObjectUrl(file);

              preview.isBinary = true;
              preview.previewUrl = objectUrl;
              const mimeTypeLabel =
                file.type ||
                gt('unknown', { context: 'file MIME type unknown' });
              const ocrNote = [
                'png',
                'jpg',
                'jpeg',
                'gif',
                'bmp',
                'tiff',
              ].includes(extension)
                ? gt(
                    'Image file - will be processed by OCR when you proceed.',
                    { context: 'rebate upload binary preview OCR note' },
                  )
                : gt('PDF file - will be processed by OCR when you proceed.', {
                    context: 'rebate upload binary preview OCR note',
                  });
              preview.content = gt(
                'File Type: {fileType}\nSize: {size}\nLast Modified: {lastModified}\n\n{ocrNote}',
                {
                  fileType: mimeTypeLabel,
                  size: preview.size,
                  lastModified: new Date(file.lastModified).toLocaleString(),
                  ocrNote,
                  context: 'rebate upload binary file preview metadata',
                },
              );
            } else {
              try {
                const text = await file.text();
                const maxChars = 5000;
                preview.content =
                  text.length > maxChars ? text.substring(0, maxChars) : text;
                preview.truncated = text.length > maxChars;
              } catch {
                preview.content = gt(
                  'Binary file - cannot display raw content',
                  { context: 'rebate upload file preview error' },
                );
              }
            }
          } catch (error) {
            const message =
              error instanceof Error
                ? error.message
                : gt('Unknown error', {
                    context: 'generic error message',
                  });
            preview.error = gt('Error reading file: {message}', {
              message,
              context: 'rebate upload file preview read error',
            });
            preview.content = gt('Unable to read file content', {
              context: 'rebate upload file preview empty content',
            });
          }

          newPreviews.push(preview);
        }

        setPreviews(newPreviews);
        setSelectedPreview(0); // Reset to first file
      };

      processFiles();
    }, [
      selectedFiles,
      selectedSheet,
      internalSelectedSheet,
      gt,
      formatFileSize,
    ]);

    if (!selectedFiles.length) {
      return null;
    }

    const currentPreview = previews[selectedPreview];

    const content = (
      <div className={isInPopup ? 'h-full w-full' : ''}>
        {!isInPopup && showCard && (
          <div className="pb-3">
            <h3 className="flex items-center gap-2 text-lg font-semibold">
              <FileTextIcon className="w-5 h-5" />
              <T context="rebate upload original file preview title">
                Original File Preview (<Num>{selectedFiles.length}</Num>{' '}
                <Plural n={selectedFiles.length} one="file" other="files" />)
              </T>
            </h3>
          </div>
        )}

        <div className={isInPopup ? 'h-full' : ''}>
          {/* File Tabs */}
          {selectedFiles.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b border-gray-200">
              {previews.map((preview, index) => (
                <Button
                  key={index}
                  variant={selectedPreview === index ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedPreview(index)}
                  className="flex items-center gap-2"
                >
                  {getFileIcon(preview.extension)}
                  {preview.name}
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {preview.size}
                  </Badge>
                </Button>
              ))}
            </div>
          )}

          {/* Current File Preview */}
          {currentPreview && (
            <div className="space-y-3">
              {/* File Info */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
                <span>
                  <T context="file preview metadata label">
                    <strong>Name:</strong> <Var>{currentPreview.name}</Var>
                  </T>
                </span>
                <span>
                  <T context="file preview metadata label">
                    <strong>Size:</strong> <Var>{currentPreview.size}</Var>
                  </T>
                </span>
                <span>
                  <T context="file preview metadata label">
                    <strong>Type:</strong> <Var>{currentPreview.type}</Var>
                  </T>
                </span>
                <span>
                  <T context="file preview metadata label">
                    <strong>Extension:</strong>{' '}
                    <Var>.{currentPreview.extension}</Var>
                  </T>
                </span>
              </div>

              {/* Error Display */}
              {currentPreview.error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded">
                  {currentPreview.error}
                </div>
              )}

              {/* Removed view mode selector - always show native preview for binary files */}

              {/* Content Preview */}
              <div className="relative">
                {currentPreview.truncated &&
                  !currentPreview.isBinary &&
                  !currentPreview.isSpreadsheet && (
                    <div className="mb-2">
                      <Badge variant="secondary">
                        <T context="file preview truncated badge">
                          Preview truncated - showing first 5000 characters
                        </T>
                      </Badge>
                    </div>
                  )}

                {/* Spreadsheet Viewer for CSV/XLSX */}
                {currentPreview.isSpreadsheet &&
                currentPreview.spreadsheetData ? (
                  <div className="border border-gray-200 rounded overflow-hidden">
                    {/* Sheet selector tabs for XLSX files with multiple sheets */}
                    {currentPreview.availableSheets &&
                      currentPreview.availableSheets.length > 1 && (
                        <div className="bg-gray-50 border-b border-gray-200 p-3">
                          <Tabs
                            value={
                              internalSelectedSheet ||
                              selectedSheet ||
                              currentPreview.availableSheets[0]
                            }
                            onValueChange={(value) =>
                              setInternalSelectedSheet(value)
                            }
                          >
                            <TabsList className="inline-flex h-9 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground">
                              {currentPreview.availableSheets.map(
                                (sheetName) => (
                                  <TabsTrigger
                                    key={sheetName}
                                    value={sheetName}
                                    className="inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-background data-[active]:text-foreground data-[active]:shadow"
                                  >
                                    <span className="truncate max-w-[120px]">
                                      {sheetName}
                                    </span>
                                  </TabsTrigger>
                                ),
                              )}
                            </TabsList>
                          </Tabs>
                        </div>
                      )}
                    <div
                      style={{
                        height: isInPopup ? '100%' : (previewHeight ?? '400px'),
                      }}
                    >
                      <Suspense
                        fallback={<Skeleton className="h-full w-full" />}
                      >
                        <SpreadsheetViewer
                          data={currentPreview.spreadsheetData}
                          loading={false}
                          error={null}
                          height="100%"
                          colHeaders={true}
                          rowHeaders={true}
                          filename={currentPreview.name}
                          showSelectionSummary={true}
                        />
                      </Suspense>
                    </div>
                  </div>
                ) : currentPreview.isBinary && currentPreview.previewUrl ? (
                  // Native Browser Preview for Binary Files
                  <div className="border border-gray-200 rounded overflow-hidden">
                    {currentPreview.extension === 'pdf' ? (
                      // PDF Viewer using embed
                      <embed
                        src={currentPreview.previewUrl}
                        type="application/pdf"
                        width="100%"
                        height={isInPopup ? '100%' : (previewHeight ?? '600px')}
                        style={isInPopup ? { minHeight: '100vh' } : {}}
                        className="bg-gray-100"
                      />
                    ) : (
                      // Image Viewer
                      <div className="bg-gray-50 p-4 flex justify-center">
                        <img
                          src={currentPreview.previewUrl}
                          alt={currentPreview.name}
                          className={`max-w-full object-contain border border-gray-300 rounded shadow-sm ${
                            isInPopup ? 'max-h-screen' : 'max-h-96'
                          }`}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove(
                              'hidden',
                            );
                          }}
                        />
                        <div className="hidden text-center p-8 text-gray-500">
                          <p>
                            <T context="file preview image load error">
                              Unable to preview this image
                            </T>
                          </p>
                          <p className="text-sm">
                            <T context="file preview filename label">
                              File: <Var>{currentPreview.name}</Var>
                            </T>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // Text-based content for other files
                  <pre className="bg-gray-50 border border-gray-200 p-4 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                    {currentPreview.content}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (isInPopup || !showCard) {
      return content;
    }

    return (
      <Card className={`${className} border-t-2 border-gray-200`}>
        <CardContent className="pt-4">{content}</CardContent>
      </Card>
    );
  },
);

RawFilePreview.displayName = 'RawFilePreview';

export default RawFilePreview;
